import React from 'react'
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, ExternalLink, MessageCircle } from 'lucide-react'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section company-info">
            <div className="logo">
              <img src="/assets/cdgp-logo-white.png" alt="Clinique du Grand Paris Logo" className="logo-image" />
              <div className="logo-text">
                <h3>Clinique du Grand Paris</h3>
              </div>
            </div>
            <p className="company-description">
              La Clinique du Grand Paris est un établissement spécialisé dans la greffe capillaire, 
              la chirurgie et la médecine esthétique. Votre beauté nous regarde.
            </p>
            <div className="social-links">
              <a href="#" className="social-link" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="#" className="social-link" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="#" className="social-link" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          <div className="footer-section navigation">
            <h4>Navigation</h4>
            <ul>
              <li><a href="#accueil" onClick={(e) => { e.preventDefault(); scrollToSection('accueil'); }}>Accueil</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Nos Services</a></li>
              <li><a href="#pourquoi-nous" onClick={(e) => { e.preventDefault(); scrollToSection('pourquoi-nous'); }}>Pourquoi nous</a></li>
              <li><a href="#galerie" onClick={(e) => { e.preventDefault(); scrollToSection('galerie'); }}>Galerie</a></li>
              <li><a href="#temoignages" onClick={(e) => { e.preventDefault(); scrollToSection('temoignages'); }}>Témoignages</a></li>
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a></li>
            </ul>
          </div>

          <div className="footer-section services">
            <h4>Nos Services</h4>
            <ul>
              <li><a href="#services">Médecine Capillaire</a></li>
              <li><a href="#services">Chirurgie Esthétique</a></li>
              <li><a href="#services">Médecine Esthétique</a></li>
              <li><a href="#services">Épilation & Détatouage</a></li>
              <li><a href="#services">Soins Visage & Corps</a></li>
              <li><a href="https://www.doctolib.fr/centre-laser-et-esthetique/paris/clinique-du-grand-paris" target="_blank" rel="noopener noreferrer">Consultation Gratuite</a></li>
            </ul>
          </div>

          <div className="footer-section contact-info">
            <h4>Contact</h4>
            <div className="contact-items">
              <div className="contact-item">
                <Phone size={16} />
                <span>01 48 66 20 35</span>
              </div>
              <div className="contact-item">
                <Mail size={16} />
                <span>secretariat@cliniquedugrandparis.com</span>
              </div>
              <div className="contact-item">
                <MapPin size={16} />
                <span>37A Avenue d'Iéna, 75116 Paris</span>
              </div>
              <div className="contact-item">
                <Clock size={16} />
                <span>Lundi-Samedi: 10h-19h</span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-ai-section">
          <div className="ai-assistant">
            <h4>Besoin d'aide immédiate ?</h4>
            <p>Chattez avec Ben, notre assistant IA spécialisé en médecine esthétique</p>
            <button 
              onClick={() => {
                if (window.openBenChat) {
                  window.openBenChat('footer-link')
                } else {
                  // Fallback: open in new window
                  window.open('https://ben.ricopoulos.com', '_blank', 'noopener,noreferrer')
                }
              }}
              className="ai-link"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              Parler avec Ben
              <MessageCircle size={16} />
            </button>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-legal">
            <div className="legal-links">
              <a href="/mentions-legales">Mentions Légales</a>
              <a href="/politique-confidentialite">Politique de Confidentialité</a>
              <a href="/cgv">CGV</a>
              <a href="/cookies">Cookies</a>
            </div>
            <div className="copyright">
              <p>&copy; {currentYear} Clinique du Grand Paris. Tous droits réservés.</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .footer {
          background: linear-gradient(135deg, #2C3E50 0%, #34495e 100%);
          color: white;
          padding: 4rem 0 0;
          position: relative;
        }

        .footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('/pattern-light.svg') repeat;
          opacity: 0.05;
          z-index: 1;
        }

        .container {
          position: relative;
          z-index: 2;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1.5fr;
          gap: 3rem;
          margin-bottom: 3rem;
        }

        .footer-section h4 {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #ffffff;
        }

        /* Ensure proper contrast for sections with light backgrounds */
        .footer-section.services,
        .footer-section.contact-info {
          background: rgba(255, 255, 255, 0.1);
          padding: 2rem;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .footer-section.services h4,
        .footer-section.contact-info h4 {
          color: white;
        }

        .footer-section.services ul li a,
        .footer-section.contact-info .contact-item {
          color: #ffffff;
        }

        .footer-section.services ul li a:hover {
          color: #8B6F47;
        }

        .company-info .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 1.5rem;
        }

        .logo-image {
          height: 40px;
          width: auto;
        }

        .logo-text h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          line-height: 1;
        }

        .logo-text span {
          font-size: 0.9rem;
          color: #ffffff;
          font-weight: 500;
        }

        .company-description {
          color: #ffffff;
          line-height: 1.6;
          margin-bottom: 2rem;
          font-weight: 400;
        }

        .social-links {
          display: flex;
          gap: 1rem;
        }

        .social-link {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .social-link:hover {
          background: #8B6F47;
          border-color: #8B6F47;
          color: #ffffff;
          transform: translateY(-2px);
        }

        .footer-section ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .footer-section ul li {
          margin-bottom: 0.75rem;
        }

        .footer-section ul li a {
          color: #ffffff;
          text-decoration: none;
          transition: color 0.3s ease;
          font-weight: 600;
        }

        .footer-section ul li a:hover {
          color: #8B6F47;
        }

        .contact-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #ffffff;
          font-size: 0.95rem;
          font-weight: 600;
        }

        .contact-item svg {
          color: #8B6F47;
          flex-shrink: 0;
        }

        .footer-ai-section {
          background: rgba(184, 155, 114, 0.15);
          border: 2px solid rgba(184, 155, 114, 0.4);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 3rem;
          text-align: center;
        }

        .ai-assistant h4 {
          font-size: 1.3rem;
          margin-bottom: 1rem;
          color: white;
        }

        .ai-assistant p {
          color: #ffffff;
          margin-bottom: 1.5rem;
          line-height: 1.5;
          font-weight: 400;
        }

        .ai-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #8B6F47;
          color: #ffffff;
          padding: 1rem 2rem;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          border: 2px solid #8B6F47;
        }

        .ai-link:hover {
          background: #ffffff;
          color: #8B6F47;
          border-color: #8B6F47;
          transform: translateY(-2px);
        }

        .footer-bottom {
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          padding: 2rem 0;
        }

        .footer-legal {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .legal-links {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .legal-links a {
          color: #ffffff;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          transition: color 0.3s ease;
        }

        .legal-links a:hover {
          color: #8B6F47;
        }

        .copyright p {
          color: #ffffff;
          font-size: 0.9rem;
          font-weight: 500;
          margin: 0;
        }

        @media (max-width: 1024px) {
          .footer-content {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
        }

        @media (max-width: 768px) {
          .footer-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .footer-legal {
            flex-direction: column;
            text-align: center;
          }

          .legal-links {
            justify-content: center;
            gap: 1rem;
          }

          .footer-ai-section {
            padding: 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .footer {
            padding: 3rem 0 0;
          }

          .legal-links {
            flex-direction: column;
            gap: 0.5rem;
          }

          .footer-ai-section {
            padding: 1rem;
          }

          .ai-link {
            padding: 0.75rem 1.5rem;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </footer>
  )
}

export default Footer